import {
    Component,
    ElementRef,
    NgZone,
    OnInit,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { Logo, ShopSettings } from '../../generic/classes/shop.settings';
import { User } from '../../generic/classes/user';
import { ConfigurationHelper } from '../../generic/helper/configuration.helper';
import { ActiveOrderService } from '../../generic/service/active.order.service';
import { ApplicationService } from '../../generic/service/application.service';
import { PlantscapingService } from '../../generic/service/plantscaping.service';
import { UserService } from '../../generic/service/user.service';
import { NavbarTabsComponent } from './navbar.tabs.component';
import { StartOrderComponent } from '../../generic/component/order/start.order.component';
import { ShowMessageComponent } from './show.message.component';
import { ShoppingcartHeaderComponent } from '../../generic/component/cart/shoppingcart.header.component';
import { ShoppingcartHeaderMobileComponent } from '../../generic/component/cart/shoppingcart.header.mobile.component';
import { LanguageSelectComponent } from './language.select.component';
import { PlanthunterAmountOfNewHuntsComponent } from '../../generic/component/planthunter/planthunter.amount.of.new.hunts.component';
import { NgClass } from '@angular/common';

/**
 * Navigation bar component. Needs user and shopping cart information.
 *
 * Contains an animation for the number of items in the cart. If it changes, it pulses
 *
 * @author evandongen
 */
@Component({
    selector: 'navigation-bar',
    styleUrls: [
        './navbar.component.scss',
        '../../../assets/style/_layout.scss',
    ],
    templateUrl: 'navbar.component.html',
    standalone: true,
    imports: [
        NgClass,
        RouterLink,
        PlanthunterAmountOfNewHuntsComponent,
        LanguageSelectComponent,
        ShoppingcartHeaderMobileComponent,
        ShoppingcartHeaderComponent,
        NavbarTabsComponent,
        ShowMessageComponent,
        StartOrderComponent,
    ],
})
export class NavbarComponent implements OnInit {
    showMobileMenu: boolean = false;
    @ViewChild(NavbarTabsComponent) navbarTabsComponent: NavbarTabsComponent;

    shopSettings: ShopSettings = ConfigurationHelper.getSettings();

    assortmentUrl: string;
    plantscapingUrl: string;
    showPlantscapingMenuItem: boolean;
    showPlantscapingMenuItemForKvk: boolean;

    constructor(
        private userService: UserService,
        public applicationService: ApplicationService,
        public activeOrderService: ActiveOrderService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private elementRef: ElementRef,
        private plantscapingService: PlantscapingService,
        private ngZone: NgZone,
    ) {}

    updateNavbarStickyness = (): void => {
        const headerElement = document.getElementById('main-header');
        const headerMenuElement = document.getElementById('header-top');

        // Set the headerIsSticky variable based on if the window scrolled past the shopping header
        if (headerElement && headerMenuElement) {
            const headerIsSticky =
                window.scrollY > headerMenuElement.clientHeight;

            if (headerIsSticky) {
                headerElement.classList.add('sticky');
            } else {
                if (headerElement.classList.contains('sticky')) {
                    headerElement.classList.remove('sticky');
                }
            }
        }
    };

    getLogo(): Logo | null {
        return this.shopSettings != null ? this.shopSettings.logo : null;
    }

    getShopName(): string {
        return this.shopSettings != null ? this.shopSettings.name : 'Noviflora';
    }

    ngOnInit() {
        if (this.getUser() != null) {
            this.ngZone.runOutsideAngular(() => {
                window.addEventListener(
                    'scroll',
                    this.updateNavbarStickyness,
                    true,
                );
            });
        }

        this.assortmentUrl = `/${ConfigurationHelper.getWebshopLanguageIso2()}/products`;

        this.plantscapingService
            .getPlantscapingUrl(this.isKvkShop())
            .then((response: any) => {
                const isWebsiteUrl = response?.startsWith('/site/');
                this.plantscapingUrl = isWebsiteUrl
                    ? `${ConfigurationHelper.getWebsiteUrl(response)}`
                    : `${response}`;
            });

        this.plantscapingService
            .hasPlantscapingPageAndIsActive()
            .then((response: any) => {
                const isKvkAndLoggedIn: boolean =
                    this.getUser() !== undefined && this.isKvkShop();
                const showPlantscapingResponse = JSON.parse(response ?? false);

                this.showPlantscapingMenuItemForKvk =
                    isKvkAndLoggedIn && showPlantscapingResponse;
                this.showPlantscapingMenuItem =
                    showPlantscapingResponse && !this.isKvkShop();

                return;
            });

        /**
         * Hamburger menu, when active, will be hidden when a navigation action is performed.
         */
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.showMobileMenu = false;
            }
        });
    }

    getUser(): User {
        return this.userService.getUser();
    }

    getMainMenuItems(): any {
        return (<any>window).mainMenuItems;
    }

    trackByMenuItem(index, menu) {
        return menu.text;
    }

    /**
     *  Only render the shopping cart if there's an order selected and we're not on a 'my' page.
     *
     */
    showShoppingCart(): boolean {
        return this.activeOrderService.hasSelectedOrder() && !this.showMyTabs();
    }

    isAssortmentActive(): boolean {
        return (
            !this.showMyTabs() &&
            !this.isBlogsActive() &&
            !this.isHuntersActive() &&
            !this.isPlantscapingActive()
        );
    }

    isPlantscapingActive(): boolean {
        return !!(
            this.navbarTabsComponent &&
            this.navbarTabsComponent.checkActive('plantscaping=1')
        );
    }

    isBlogsActive(): boolean {
        return !!(
            this.navbarTabsComponent &&
            this.navbarTabsComponent.checkActive('blogs')
        );
    }

    isHuntersActive(): boolean {
        return !!(
            this.navbarTabsComponent &&
            this.navbarTabsComponent.checkActive('hunters')
        );
    }

    isKvkShop() {
        return this.shopSettings != null && this.shopSettings.kvkShop;
    }

    isHunterMenuItem(menuItem: string): boolean {
        return menuItem === 'Hunters' || menuItem === 'Planthunters';
    }

    showHuntersForKvkShop(): boolean {
        return this.shopSettings != null && this.shopSettings.showHunters;
    }

    viewPendingOrders() {
        this.router.navigate(['/my/pending/orders']);
    }

    viewShoppingcart() {
        this.router.navigate(['/products/shoppingcart']);
    }

    viewOrderDetail() {
        const activeOrder = this.activeOrderService.getSavedActiveOrder();

        if (activeOrder !== null) {
            this.router.navigate([
                '/my/pending/orders/detail',
                {
                    shipmentDate: activeOrder.shipmentDate,
                    deliveryAddressCode: activeOrder.shipToCode,
                },
            ]);
        }
    }

    toggleMobileMenu() {
        this.showMobileMenu = !this.showMobileMenu;
    }

    showMyTabs(): boolean {
        return !!(
            this.navbarTabsComponent && this.navbarTabsComponent.showMyTabs()
        );
    }
}
